/* eslint-disable max-lines */
import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import { Link as RouterLink, Redirect, useHistory } from 'react-router-dom';
import _ from 'lodash';
import axios, { isAxiosError } from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import type { Moment } from 'moment';
import moment from 'moment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import OriginalCircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import NativeSelect from '@material-ui/core/NativeSelect';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
// import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import Paper from '@material-ui/core/Paper';
import Slider from '@material-ui/core/Slider';
import IconButton from '@material-ui/core/IconButton';
import HelpIcon from '@material-ui/icons/Help';
import SettingsIcon from '@material-ui/icons/Settings';
import AppsIcon from '@material-ui/icons/Apps';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import CloseIcon from '@material-ui/icons/Close';
import CancelIcon from '@material-ui/icons/Cancel';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DateRangeIcon from '@material-ui/icons/DateRange';
import Carousel from 'react-material-ui-carousel';
import muiTheme from '@this/components/appoint/mui_theme';
import type { AdjustmentMeetingType, AdjustmentResponseArgs } from '@this/domain/appoint/adjustment';
import Adjustment, {
  convertAdjustmentResponseToArgs,
  initialAdjustmentArgs
} from '@this/domain/appoint/adjustment';
import type { CalendarArgs } from '@this/domain/appoint/calendar';
import Calendar from '@this/domain/appoint/calendar';
import type { CandidateResponseArgs } from '@this/domain/appoint/candidate';
import Candidate, { convertCandidateResponseToArgs } from '@this/domain/appoint/candidate';
import type { MeetingTitleHistoryArgs } from '@this/domain/appoint/meeting_title_history';
import MeetingTitleHistory from '@this/domain/appoint/meeting_title_history';
import type { EventResponse } from '@this/domain/appoint/event';
import Event, { convertEventResponseToArgs } from '@this/domain/appoint/event';
import type { UserSettingResponse } from '@this/domain/appoint/user_setting';
import UserSetting, { convertUserSettingResponseToArgs } from '@this/domain/appoint/user_setting';
import HoverActionBox from '@this/components/appoint/hover_action_box';
import TextField from '@material-ui/core/TextField';
import Utils from '@this/domain/appoint/utils';
import AppointContext from '@this/components/appoint/appoint_context';
import localStorage from '@this/lib/local_storage';
import BigCalendar from './new_big_calendar';
import PlaceInput from './place_input';
import ParticipantInput from './participant_input';
import NewCoachMark from './new_coach_mark';
import type { Plan } from '../../common';
import { H2, Wrapper, Left, Right, Section, FlatButton, Backdrop } from '../../common';

const ADJUSTMENT_KEY = 'adjustmentDataV1';

interface Response {
  need_auth: boolean;
  primary_calendar_id: string;
  calendars: CalendarArgs[];
  disconnected_accounts: { email: string; message: string | null }[];
  current_plan: Plan;
  trial_used: boolean;
  user_setting: UserSettingResponse;
  meeting_title_histories: MeetingTitleHistoryArgs[];
  need_coach_mark: boolean;
}

interface CreateResponse {
  adjustment: {
    key?: string;
    error?: string;
  };
  return_url: string;
}

interface EventsResponse {
  events: EventResponse[][];
}

interface SuggestParams {
  calendars: { id: string; auth_data_id: string }[];
  from: string;
  to: string;
  max_suggest_count: number;
  target_start_hour: number;
  target_end_hour: number;
  duration: number;
  max_candidate_hours: number;
  meeting_place: string;
  default_event_place?: string;
  default_event_place_address?: string;
  suggest_count_in_day: number;
  suggest_weekday?: string;
  calendar_checked?: string;
  share_html_with_head: boolean;
  share_html_with_foot: boolean;
  share_html_slashed_date: boolean;
  suggest_holiday: boolean;
}

interface SuggestResponse {
  candidates: CandidateResponseArgs[];
  travel_times: CandidateResponseArgs[];
}

const durations = [
  { label: '15分', value: 15 },
  { label: '30分', value: 30 },
  { label: '45分', value: 45 },
  { label: '60分', value: 60 },
  { label: '90分', value: 90 },
  { label: '120分', value: 120 },
  { label: '180分', value: 180 }
];

const createDefaultUserSetting = () => {
  return new UserSetting({
    maxSuggestCount: 3,
    targetStartHour: 9,
    targetEndHour: 18,
    maxCandidateHours: 3,
    suggestCountInDay: 1,
    suggestWeekday: '0111110',
    suggestHoliday: true,
    meetingType: 'none'
  });
};

const createDefaultAdjustment = (paramName: string | null) => {
  const adustmentKey = localStorage.getItem(ADJUSTMENT_KEY);
  if ((paramName === '' || paramName === null) && adustmentKey !== null) {
    const raw: AdjustmentResponseArgs = JSON.parse(adustmentKey);
    // TODO: 参加者をlocalStorageに保存し、getItemするとメールアドレスが消えてしまう。（セキュリティ？）
    //       chromeで確認すると保存はされている。そのため一旦参加者は無視する。
    raw.participants = [];
    return new Adjustment(convertAdjustmentResponseToArgs(raw));
  }
  return new Adjustment(initialAdjustmentArgs);
};

const AdjustmentsNew = observer(() => {
  const history = useHistory();
  const getParams = new URLSearchParams(window.location.search);
  const paramRefApp = getParams.get('ref_app');
  const paramOrgnzID = getParams.get('organization_id');
  const paramAppID = getParams.get('application_id');
  let paramName = getParams.get('name');

  const contextValue = useContext(AppointContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [adjustment] = useState<Adjustment>(createDefaultAdjustment(paramName));
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [disconnectedAccounts, setDisconnectedAccounts] = useState<{ email: string; message: string | null }[]>(
    []
  );
  const [eventLists, setEventLists] = useState<Event[][]>([[]]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [needAuth, setNeedAuth] = useState<boolean>(false);
  const [needReAuth, setNeedReAuth] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [date, setDate] = useState<Moment>(moment());
  const [isEventLoading, setIsEventLoading] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<Plan>('free');
  const [trialUsed, setTrialUsed] = useState<boolean>(false);
  const [isSuggestLoading, setIsSuggetsLoading] = useState<boolean>(false);
  const [suggestCandidatesError, setSuggestCandidatesError] = useState<string | null>(null);
  const [travelTimes, setTravelTimes] = useState<Candidate[]>([]);
  const [meetingTitleHistories, setMeetingTitleHistories] = useState<MeetingTitleHistory[]>([]);
  const [showSuggestSettingDialog, setShowSuggestSettingDialog] = useState<boolean>(false);
  const [showSuggestDetailSetting, setShowSuggestDetailSetting] = useState<boolean>(false);
  const [showMobileCalendarDialog, setShowMobileCalendarDialog] = useState<boolean>(false);
  const [showCandidateHelpDialog, setShowCandidateHelpDialog] = useState<boolean>(false);
  const [suggestCandidatesTimerID, setSuggestCandidatesTimerID] = useState<number>(0);
  const [isSuggestCandidatesLoading, setIsSuggestCandidatesLoading] = useState<boolean>(false);
  const [showZoomAuthDialog, setShowZoomAuthDialog] = useState<boolean>(false);
  const [needCoachMark, setNeedCoachMark] = useState<boolean>(false);

  const theLeftBoxRef = useRef<HTMLDivElement>(null);
  const inputFormRef = useRef(null);
  const suggestSettingButtonRef = useRef(null);
  const suggestButtonRef = useRef(null);

  // 開始日にaddEndDateを加算した結果を終了日とする。終了日なしの場合は、一旦14日を設定する。
  // 候補日が不足する場合のための再試行処理がサーバー側にある。
  const addEndDate = 13;
  const suggestCandidatesInterval = 3000;

  // カレンダーが月曜始まりなので、startMomentには「今週の月曜日」を表示したい。
  // 2021/06/06(日)時点では、2021/05/31(月)をstartMomentにセットしたい。
  // 2021/06/07(月)時点では、2021/06/07(月)をstartMomentにセットしたい。
  // day(1)は、曜日番号を1に変更するだけなので、日曜日(0)から見て前週の月曜日を取得するためには、
  // 1日戻る必要があるためsubtract(1, 'days')を設定している。
  const startMoment = date.clone().subtract(1, 'days').day(1);
  const endMoment = startMoment.clone().add(addEndDate, 'days');
  const startDate = startMoment.format('YYYY-MM-DD');
  const endDate = endMoment.format('YYYY-MM-DD');

  // 候補日自動計算用
  const [targetFromDate, setTargetFromDate] = useState<Moment>(moment().add(1, 'day'));
  const [userSetting, setUserSetting] = useState<UserSetting>(createDefaultUserSetting());

  const substructEvent = () => {
    const newEventLists: Event[][] = [[]];
    adjustment.eventlists.forEach(evts => {
      let existflg = false;
      evts.forEach(evt => {
        adjustment.participants.forEach(cal => {
          if (cal.id === evt.calendarId) {
            existflg = true;
          }
        });
      });
      if (existflg) {
        newEventLists.push(evts);
      }
    });
    return newEventLists;
  };

  const differentCalendars = () => {
    const diffCalendars: Calendar[] = [];
    adjustment.participants.forEach(cal => {
      let existflg = false;
      adjustment.eventlists.forEach(evts => {
        evts.forEach(evt => {
          if (cal.id === evt.calendarId) {
            existflg = true;
          }
        });
      });
      if (existflg === false) {
        diffCalendars.push(cal);
      }
    });
    return diffCalendars;
  };

  const fetchEvents = useCallback(
    async (onDateChange?: boolean) => {
      if (adjustment.participants.length === 0) {
        setEventLists([]);
        adjustment.setEventlists([[]]);
        return;
      }
      setIsEventLoading(true);
      const tmpEventLists = substructEvent();
      const targetCalendars = onDateChange ? adjustment.participants : differentCalendars();
      if (targetCalendars.length > 0) {
        const params = {
          calendars: targetCalendars.map(p => (p ? { id: p.id, auth_data_id: p.authDataId } : null)),
          from: startDate,
          to: endDate
        };
        const res = await axios.get<EventsResponse>('/aicalendar/events.json', { params });
        const data = res.data;
        const addEvents = data.events.map(evts => evts.map(e => new Event(convertEventResponseToArgs(e))));
        const existingIds = _.map(tmpEventLists.flat(), e => e.id);
        addEvents.forEach(dataOne => {
          tmpEventLists.push(_.filter(dataOne, d => existingIds.indexOf(d.id) === -1));
        });
      }
      setEventLists(tmpEventLists);
      adjustment.setEventlists(tmpEventLists);
      setIsEventLoading(false);
    },
    [adjustment.participants, startDate, endDate]
  );

  // 候補日の自動選択
  const handleSuggestCandidates = async (fromDate: Moment) => {
    Utils.trackEvent('[cal][click] suggest candidates');
    setIsSuggestCandidatesLoading(true);
    setIsSuggetsLoading(true);
    setSuggestCandidatesError(null);
    const params: SuggestParams = {
      ...userSetting.submitParams(),
      ...{
        calendars: adjustment.participants.map(p => ({
          id: p.id,
          auth_data_id: p.authDataId
        })),
        from: fromDate.format('YYYY-MM-DD'),
        to: fromDate.clone().add(addEndDate, 'days').format('YYYY-MM-DD'),
        duration: adjustment.duration,
        meeting_place: adjustment.placeAddress || adjustment.place
      }
    };
    try {
      const res = await axios.get<SuggestResponse>('/aicalendar/suggest_candidates.json', { params });
      adjustment.candidates = res.data.candidates.map(
        c => new Candidate(convertCandidateResponseToArgs(c, contextValue.isMobile))
      );
      setTravelTimes(res.data.travel_times.map(t => new Candidate(convertCandidateResponseToArgs(t))));
      if (adjustment.candidates.length > 0) {
        setDate(adjustment.candidates[0].start);
      }
    } catch (e) {
      setSuggestCandidatesError('通信エラーが発生しました。時間をおいて再度お試しください。');
    }
    setIsSuggetsLoading(false);
    setIsSuggestCandidatesLoading(false);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const res = await axios.get<Response>('/aicalendar/adjustments/new.json');
    setIsLoading(false);
    if (res.data.need_auth) {
      setNeedAuth(true);
      return;
    }
    setDisconnectedAccounts(res.data.disconnected_accounts);
    if (res.data.disconnected_accounts.length > 0) {
      setNeedReAuth(true);
      return;
    }

    const calendars = res.data.calendars.map(c => new Calendar(c));
    setCalendars(calendars);
    if (adjustment.participants === null || adjustment.participants.length === 0) {
      const primary = calendars.find(c => c.id === res.data.primary_calendar_id) || null;
      if (primary) {
        adjustment.participants = [primary];
      }
    }
    if (adjustment && paramName) {
      adjustment.title = paramName;
      paramName = '';
    }
    setCurrentPlan(res.data.current_plan);
    setTrialUsed(res.data.trial_used);
    if (res.data.user_setting) {
      setUserSetting(new UserSetting(convertUserSettingResponseToArgs(res.data.user_setting)));
      adjustment.meetingType = res.data.user_setting.meeting_type;
    }
    setMeetingTitleHistories(res.data.meeting_title_histories.map(raw => new MeetingTitleHistory(raw)));
    setNeedCoachMark(res.data.need_coach_mark);
  }, [adjustment.participants]);

  // マウント時に1回発動
  useEffect(() => {
    fetchData();
    Utils.trackEvent('[cal][view] adjustments new');
  }, []);

  useEffect(() => {
    fetchEvents(false);
  }, [adjustment.participants]);

  useEffect(() => {
    fetchEvents(true);
  }, [startDate, endDate]);

  useEffect(() => {
    localStorage.setItem(ADJUSTMENT_KEY, JSON.stringify(adjustment.submitParams()));
  }, [
    adjustment.title,
    adjustment.place,
    adjustment.placeAddress,
    adjustment.description,
    adjustment.startTime,
    adjustment.endTime,
    adjustment.duration,
    adjustment.meetingType,
    adjustment.participants
  ]);

  const handleAddParticipant = (participant: Calendar) => {
    adjustment.participants.push(participant);
    fetchEvents();
  };

  const handleRemoveParticipant = (participant: Calendar) => {
    adjustment.removeParticipant(participant);
    fetchEvents();
  };

  const handleTargetTimeRangeChange = (values: number | number[]) => {
    if (typeof values === 'number') {
      return;
    }
    userSetting.targetStartHour = values[0];
    userSetting.targetEndHour = values[1];
  };

  const handleTargetFromDateChange = (date: Moment | null) => {
    if (date === null) {
      return;
    }

    setTargetFromDate(date);
    setIsSuggestCandidatesLoading(true);
    window.clearTimeout(suggestCandidatesTimerID);
    setSuggestCandidatesTimerID(
      window.setTimeout(() => {
        handleSuggestCandidates(date);
      }, suggestCandidatesInterval)
    );
  };

  const handleChangeDuration = (e: React.ChangeEvent<{ value: unknown }>) => {
    if (typeof e.target.value === 'string') {
      adjustment.duration = parseInt(e.target.value, 10);
    } else if (typeof e.target.value === 'number') {
      adjustment.duration = e.target.value;
    }
    adjustment.candidates = adjustment.candidates.filter(
      c => c.end.diff(c.start, 'minutes') >= adjustment.duration
    );
  };

  const handleSubmit = async () => {
    Utils.trackEvent('[cal][click] create adjustment');
    setIsSubmitting(true);
    try {
      axios.post('/search_histories', {
        histories: [
          {
            main_text: adjustment.place,
            secondary_text: '',
            kind: 'calendar'
          }
        ]
      });
      adjustment.refApp = paramRefApp || '';
      adjustment.organizationId = paramOrgnzID || '';
      adjustment.applicationId = paramAppID || '';
      const res = await axios.post<CreateResponse>('/aicalendar/adjustments.json', adjustment.submitParams());
      if (res.data.adjustment.error) {
        setErrorMessage(res.data.adjustment.error);
      } else {
        adjustment.key = res.data.adjustment.key;
        localStorage.removeItem(ADJUSTMENT_KEY);
        if (res.data.return_url) {
          location.href = history.createHref({ pathname: '/external_connections' });
        }
      }
    } catch (e) {
      if (isAxiosError(e) && e.response?.data?.message) {
        setErrorMessage(e.response.data.message);
      } else {
        setErrorMessage('通信エラーが発生しました');
      }
    }

    setIsSubmitting(false);
  };

  const handleValidation = () => {
    if (adjustment.title.length > 0) {
      setErrorMessage(null);
      handleSubmit();
    } else {
      setErrorMessage('打ち合わせのタイトルを入力してください。');
    }
  };

  const isParticipantEmpty = () => {
    return adjustment.participants.length === 0;
  };

  const isCandidatesEmpty = () => {
    return adjustment.candidates.length === 0;
  };

  const isMultiParticipantPrevented = () => {
    // 当面無料で全機能使えるようにする
    return false;

    // eslint-disable-next-line no-unreachable
    return currentPlan === 'free' && adjustment.participants.length > 1;
  };

  const isTitleEmpty = () => {
    return adjustment.title.length === 0;
  };

  const isSubmitButtonAvailable = () => {
    return (
      !isSubmitting &&
      !isCandidatesEmpty() &&
      !isParticipantEmpty() &&
      !isMultiParticipantPrevented() &&
      !isTitleEmpty()
    );
  };

  const isSuggestWeekday = (pos: number) => {
    return userSetting.suggestWeekday?.substring(pos, pos + 1) === '1';
  };

  const handleUpdateSuggestWeekday = (pos: number) => {
    let suggestWeekday = userSetting.suggestWeekday?.substring(pos, pos + 1);
    if (suggestWeekday === '1') {
      suggestWeekday = '0';
    } else {
      suggestWeekday = '1';
    }
    switch (pos) {
      case 0:
        userSetting.suggestWeekday = suggestWeekday + (userSetting.suggestWeekday?.substring(pos + 1, 7) ?? 0);
        break;
      case 6:
        userSetting.suggestWeekday = (userSetting.suggestWeekday?.substring(0, pos) ?? 0) + suggestWeekday;
        break;
      default:
        userSetting.suggestWeekday =
          (userSetting.suggestWeekday?.substring(0, pos) ?? 0) +
          suggestWeekday +
          (userSetting.suggestWeekday?.substring(pos + 1, 7) ?? 0);
        break;
    }
  };

  const timeStr = (v: number) => {
    const h = Math.floor(v);
    const m = 60 * (v - h);
    return m === 0 ? `${h}時` : `${h}時${m}分`;
  };

  const user = contextValue.user;

  const handleChangeMeetingType = (meetingType: AdjustmentMeetingType) => {
    if (meetingType !== 'zoom' || user?.appointZoomAccessToken) {
      adjustment.meetingType = meetingType;
      userSetting.meetingType = meetingType;
      axios.put('/aicalendar/user_settings', userSetting.submitParams());
    } else {
      setShowZoomAuthDialog(true);
    }
  };

  const renderSuggestSettingDialog = () => (
    <Dialog
      open={showSuggestSettingDialog}
      onClose={() => setShowSuggestSettingDialog(false)}
      PaperProps={{
        style: {
          width: '100%',
          margin: '30px 20px 20px'
        }
      }}
    >
      <Paper style={{ position: 'relative', paddingTop: '35px' }}>
        <IconButton
          size="small"
          onClick={() => setShowSuggestSettingDialog(false)}
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <RuleHead>
          <RuleHeadIcon>
            <AppsIcon fontSize="large" />
          </RuleHeadIcon>
          <RuleTitle>条件設定</RuleTitle>
          <RuleText>候補日の自動抽出ルールをカスタマイズ</RuleText>
        </RuleHead>
        <RuleMain isMobile={contextValue.isMobile}>
          <Section>
            <Box display="flex">
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <DatePicker
                  autoOk
                  disableToolbar
                  disablePast
                  variant="inline"
                  format="YYYY/MM/DD(dd)"
                  margin="none"
                  value={targetFromDate}
                  onChange={date => handleTargetFromDateChange(date)}
                  style={{ marginRight: '20px' }}
                />
              </MuiPickersUtilsProvider>
              <LabelSuggest>以降</LabelSuggest>
            </Box>
          </Section>
          <Section style={{ textAlign: 'center' }}>
            <Box>
              <ToggleButton
                type="checkbox"
                data-off-label="月"
                data-on-label="月"
                value="1"
                checked={isSuggestWeekday(1)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(1);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <ToggleButton
                type="checkbox"
                data-off-label="火"
                data-on-label="火"
                value="1"
                checked={isSuggestWeekday(2)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(2);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <ToggleButton
                type="checkbox"
                data-off-label="水"
                data-on-label="水"
                value="1"
                checked={isSuggestWeekday(3)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(3);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <ToggleButton
                type="checkbox"
                data-off-label="木"
                data-on-label="木"
                value="1"
                checked={isSuggestWeekday(4)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(4);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <ToggleButton
                type="checkbox"
                data-off-label="金"
                data-on-label="金"
                value="1"
                checked={isSuggestWeekday(5)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(5);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <ToggleButton
                type="checkbox"
                data-off-label="土"
                data-on-label="土"
                value="1"
                checked={isSuggestWeekday(6)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(6);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <ToggleButton
                type="checkbox"
                data-off-label="日"
                data-on-label="日"
                value="1"
                checked={isSuggestWeekday(0)}
                isMobile={contextValue.isMobile}
                onClick={() => {
                  handleUpdateSuggestWeekday(0);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
            </Box>
          </Section>
          <HourSelectSection>
            <Box>
              {timeStr(userSetting.targetStartHour)}〜{timeStr(userSetting.targetEndHour)}
            </Box>
            <Slider
              value={[userSetting.targetStartHour, userSetting.targetEndHour]}
              min={0}
              max={24}
              marks={[0, 3, 6, 9, 12, 15, 18, 21, 24].map(v => {
                return { value: v, label: `${v}時` };
              })}
              onChange={(_, values) => {
                handleTargetTimeRangeChange(values);
                setIsSuggestCandidatesLoading(true);
                window.clearTimeout(suggestCandidatesTimerID);
                setSuggestCandidatesTimerID(
                  window.setTimeout(() => {
                    handleSuggestCandidates(targetFromDate);
                  }, suggestCandidatesInterval)
                );
              }}
              valueLabelDisplay="auto"
              aria-labelledby="discrete-slider-always"
              getAriaValueText={v => timeStr(v)}
              step={0.5}
            />
          </HourSelectSection>
          <Section>
            <Box display="flex">
              <LabelSuggest>上記の条件で</LabelSuggest>
              <TextField
                className="appoint-new__text-suggest-count"
                type="number"
                value={userSetting.maxSuggestCount}
                onChange={e => {
                  userSetting.maxSuggestCount = parseInt(e.target.value, 10);
                  setIsSuggestCandidatesLoading(true);
                  window.clearTimeout(suggestCandidatesTimerID);
                  setSuggestCandidatesTimerID(
                    window.setTimeout(() => {
                      handleSuggestCandidates(targetFromDate);
                    }, suggestCandidatesInterval)
                  );
                }}
              />
              <LabelSuggest>個の候補を提案する</LabelSuggest>
            </Box>
          </Section>
        </RuleMain>
        <LabelSuggestDescription rotate={showSuggestDetailSetting ? '180' : '0'}>
          <span className="text" onClick={() => setShowSuggestDetailSetting(!showSuggestDetailSetting)}>
            詳細条件
            <span className="icon">
              <ArrowDropDownIcon color="primary" />
            </span>
          </span>
        </LabelSuggestDescription>
        <RuleDetail opened={`${showSuggestDetailSetting}`}>
          <Section>
            <Box display="flex" style={{ justifyContent: 'center' }}>
              <RuleFormControl>
                <RuleInputLabel id="duration">打ち合わせの長さ</RuleInputLabel>
                <Select
                  labelId="duration"
                  value={adjustment.duration}
                  onChange={e => {
                    if (typeof e.target.value === 'string') {
                      adjustment.duration = parseInt(e.target.value, 10);
                    } else if (typeof e.target.value === 'number') {
                      adjustment.duration = e.target.value;
                    }
                    setIsSuggestCandidatesLoading(true);
                    window.clearTimeout(suggestCandidatesTimerID);
                    setSuggestCandidatesTimerID(
                      window.setTimeout(() => {
                        handleSuggestCandidates(targetFromDate);
                      }, suggestCandidatesInterval)
                    );
                  }}
                >
                  {durations.map(d => (
                    <MenuItem value={d.value} key={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </RuleFormControl>
              <RuleFormControl>
                <TextField
                  label="1日の最大提案数"
                  InputLabelProps={{ style: { fontSize: '10px' } }}
                  type="number"
                  value={userSetting.suggestCountInDay}
                  onChange={e => {
                    userSetting.suggestCountInDay = parseInt(e.target.value, 10);
                    setIsSuggestCandidatesLoading(true);
                    window.clearTimeout(suggestCandidatesTimerID);
                    setSuggestCandidatesTimerID(
                      window.setTimeout(() => {
                        handleSuggestCandidates(targetFromDate);
                      }, suggestCandidatesInterval)
                    );
                  }}
                />
              </RuleFormControl>
              <RuleFormControl>
                <RuleInputLabel id="targetStartHour">1枠の最長時間</RuleInputLabel>
                <Select
                  labelId="targetStartHour"
                  value={userSetting.maxCandidateHours}
                  onChange={e => {
                    if (typeof e.target.value === 'string') {
                      userSetting.maxCandidateHours = parseInt(e.target.value, 10);
                    } else if (typeof e.target.value === 'number') {
                      userSetting.maxCandidateHours = e.target.value;
                    }
                    setIsSuggestCandidatesLoading(true);
                    window.clearTimeout(suggestCandidatesTimerID);
                    setSuggestCandidatesTimerID(
                      window.setTimeout(() => {
                        handleSuggestCandidates(targetFromDate);
                      }, suggestCandidatesInterval)
                    );
                  }}
                >
                  {[...Array(12)]
                    .map((_: undefined, i: number) => i + 1)
                    .map(n => (
                      <MenuItem value={n} key={n}>
                        {n}時間
                      </MenuItem>
                    ))}
                </Select>
              </RuleFormControl>
            </Box>
          </Section>
          <Section>
            <Box style={{ textAlign: 'center', marginTop: '-13px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userSetting.suggestHoliday}
                    onChange={e => {
                      userSetting.suggestHoliday = e.target.checked;
                      setIsSuggestCandidatesLoading(true);
                      window.clearTimeout(suggestCandidatesTimerID);
                      setSuggestCandidatesTimerID(
                        window.setTimeout(() => {
                          handleSuggestCandidates(targetFromDate);
                        }, suggestCandidatesInterval)
                      );
                    }}
                    style={{ padding: '3px', color: muiTheme.palette.primary.main }}
                  />
                }
                label="候補日から祝日を除外する"
                style={{ margin: '0' }}
              />
            </Box>
          </Section>
        </RuleDetail>
        <Box style={{ padding: '26px 0', background: '#f9f9f9' }}>
          <Box display="flex">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowSuggestSettingDialog(false)}
              style={{ margin: '0 auto' }}
            >
              抽出条件を保存する
            </Button>
          </Box>
          {suggestCandidatesError && (
            <Typography color="error" style={{ fontSize: '12px' }}>
              {suggestCandidatesError}
            </Typography>
          )}
        </Box>
      </Paper>
    </Dialog>
  );

  const renderMobileCalendarDialog = () => (
    <Dialog
      open={showMobileCalendarDialog}
      maxWidth="lg"
      PaperProps={{
        style: {
          width: '100%',
          height: 'calc(100% - 30px - 20px)',
          maxHeight: 'calc(100% - 30px - 20px)',
          margin: '30px 10px 20px',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }
      }}
      onClose={() => setShowMobileCalendarDialog(false)}
    >
      <Paper style={{ position: 'relative', maxWidth: '800px' }}>
        <Box display="flex" style={{ padding: 10 }}>
          <Typography style={{ marginRight: 'auto', fontSize: 14, fontWeight: 700 }}>
            候補日をドラッグで選択
          </Typography>
          <IconButton size="small" onClick={() => setShowMobileCalendarDialog(false)}>
            <CloseButton fontSize="small" />
          </IconButton>
        </Box>
        <BigCalendar
          adjustment={adjustment}
          eventLists={eventLists}
          candidates={adjustment.candidates}
          travelTimes={travelTimes}
          calendars={calendars}
          date={date}
          isEventLoading={isEventLoading}
          onCandidatesChange={(candidates, travelTimes) => {
            adjustment.candidates = candidates;
            if (travelTimes) {
              setTravelTimes(travelTimes);
            }
          }}
          onDateChange={date => setDate(date)}
        />
        <MobileCalendarButtonWrap isMobile={contextValue.isMobile}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => setShowMobileCalendarDialog(false)}
            style={{ width: '100%', padding: 10, background: '#fff' }}
          >
            閉じる
          </Button>
        </MobileCalendarButtonWrap>
      </Paper>
    </Dialog>
  );

  const renderCandidateHelpDialog = () => (
    <Dialog open={showCandidateHelpDialog} onClose={() => setShowCandidateHelpDialog(false)} maxWidth="lg">
      <Paper style={{ position: 'relative', maxWidth: '800px' }}>
        <IconButton
          size="small"
          onClick={() => setShowCandidateHelpDialog(false)}
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Carousel navButtonsAlwaysVisible autoPlay={false}>
          <CandidateHelpBox>
            <CandidateHelpTitle>AIカレンダーとは（1）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/help1.png" />
            <CandidateHelpDescription>
              AIカレンダーは、他社との日程調整でこちらから候補日程を提案する際に便利なサービスです。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>AIカレンダーとは（2）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/help2.png" />
            <CandidateHelpDescription>
              特に複数人の日程調整は考えなければいけないことが多く面倒ですよね・・・
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>AIカレンダーとは（3）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/help3.png" />
            <CandidateHelpDescription>
              AIカレンダーを使えば、面倒な日程調整が徹底的に合理化されます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（1）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help1.png" />
            <CandidateHelpDescription>
              カレンダー上の任意の場所をクリックすると候補日が追加されます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（2）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help2.png" />
            <CandidateHelpDescription>
              カレンダー上の任意の場所をドラッグすると、ドラッグした範囲の候補日が追加されます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（3）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help3.png" style={{ maxWidth: '370px' }} />
            <CandidateHelpDescription>
              カレンダー上の候補日の上端、または下端をドラッグすることで、候補日の
              開始時間・終了時間を変更することができます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（4）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help4.png" />
            <CandidateHelpDescription>
              左カラムの候補日の「×」ボタンをクリックすると、その候補日を削除できます。
              <br />
              カレンダー上の候補日をダブルクリックすることでも削除できます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（5）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help5.png" />
            <CandidateHelpDescription>
              「候補日の自動抽出」ボタンで、参加者の空き予定を見て自動で候補日が提案されます。
              <br />
              提案後に参加者を変更した場合は、「候補日を再抽出」をクリックすることで、新しい参加者の空き予定を見て何度でも提案し直すことができます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（6）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help6.png" />
            <CandidateHelpDescription>
              歯車のアイコンをクリックして、自動抽出のルールを詳細に設定することができます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（7）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help7.png" style={{ maxWidth: '300px' }} />
            <CandidateHelpDescription>
              打ち合わせ場所を入力することで、その場所への移動時間も考慮した候補日を自動抽出することができます。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（8）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help8.png" />
            <CandidateHelpDescription>
              入力された打ち合わせ場所と、既存の予定に登録されている場所の情報から移動時間を自動計算します。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（9）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help9.png" />
            <CandidateHelpDescription>
              既存の予定に場所が登録されていない場合は、その予定との移動時間の計算を行いません。
            </CandidateHelpDescription>
          </CandidateHelpBox>
          <CandidateHelpBox>
            <CandidateHelpTitle>候補日の選び方（10）</CandidateHelpTitle>
            <CandidateHelpImg src="/images/aicalendar/candidate_help10.png" />
            <CandidateHelpDescription>
              ただし、自動抽出ルールの「普段いる場所」を入力している場合は、既存の予定に場所が登録されていなくても「普段いる場所」との移動時間を計算します。
            </CandidateHelpDescription>
          </CandidateHelpBox>
        </Carousel>
      </Paper>
    </Dialog>
  );

  const renderZoomAuthDialog = () => (
    <ZoomAuthDialog open={showZoomAuthDialog} onClose={() => setShowZoomAuthDialog(false)}>
      <Paper style={{ position: 'relative' }}>
        <IconButton
          size="small"
          onClick={() => setShowZoomAuthDialog(false)}
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <ZoomAuthContent>
          <img
            src="/images/aicalendar/zoom_auth.png"
            height="40"
            alt="Zoomと連携する"
            style={{ display: 'block' }}
          />
          <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.2em' }}>
            Zoomにログインして
            <br />
            連携をしますか？
          </p>
        </ZoomAuthContent>
        <ZoomAuthButtonWrap>
          <Button variant="contained" onClick={() => setShowZoomAuthDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.open(history.createHref({ pathname: '/zoom/authorization/new' }));
            }}
          >
            連携
          </Button>
        </ZoomAuthButtonWrap>
      </Paper>
    </ZoomAuthDialog>
  );

  return (
    <Wrapper style={{ minHeight: 'calc(100vh - 64px)' }}>
      <AuthBackdrop open={needAuth}>
        <Box>
          <Typography style={{ textAlign: 'center' }}>ログインしてGoogle Calendarと連携</Typography>
          <Button
            style={{ display: 'block', margin: '0 auto' }}
            onClick={() => {
              window.open(history.createHref({ pathname: '/auth/new?provider=google' }));
            }}
          >
            <img src="/images/sign_in_with_google.png" width="191" />
          </Button>
        </Box>
        <Box>
          <Typography style={{ textAlign: 'center' }}>ログインしてOutlook Calendarと連携</Typography>
          <Button
            style={{ display: 'block', margin: '0 auto' }}
            onClick={() => {
              window.open(history.createHref({ pathname: '/auth/new?provider=outlook' }));
            }}
          >
            <img src="/images/sign_in_with_microsoft.png" width="215" />
          </Button>
        </Box>
      </AuthBackdrop>
      <AuthBackdrop open={needReAuth}>
        <Box>
          <Typography style={{ textAlign: 'center' }}>
            {disconnectedAccounts.map((da, i) => (
              <p key={i}>
                {da.message
                  ? `Google Calendar(${da.email})への${da.message}`
                  : `Google Calendar(${da?.email})との連携が解除されています`}
              </p>
            ))}
          </Typography>
          <TheReAuthButton
            variant="contained"
            color="primary"
            style={{
              display: 'block',
              margin: '12px auto 0',
              textTransform: 'none'
            }}
            onClick={() => {
              window.open(history.createHref({ pathname: '/auth/new?provider=google' }));
            }}
          >
            Google Calendarと再連携
          </TheReAuthButton>
        </Box>
      </AuthBackdrop>
      <TheLeft isMobile={contextValue.isMobile}>
        <TheLeftBox ref={theLeftBoxRef} isMobile={contextValue.isMobile}>
          <div ref={inputFormRef}>
            <Box display="flex" alignItems="flex-start">
              <H2 style={{ marginRight: 'auto', fontSize: '16px' }}>日程調整</H2>
              <IconButton size="small" component={RouterLink} to="/">
                <CloseButton fontSize="small" />
              </IconButton>
              <Button
                startIcon={<HelpIcon />}
                size="small"
                onClick={() => setShowCandidateHelpDialog(true)}
                style={{ display: 'none' }}
              >
                ヘルプ
              </Button>
            </Box>
            <LeftSection>
              <Box display="flex">
                <AutocompleteBox withAlert={`${isTitleEmpty()}`}>
                  <Autocomplete
                    options={meetingTitleHistories}
                    getOptionLabel={history => history.title || ''}
                    inputValue={adjustment.title}
                    filterOptions={(options, state) => {
                      const q = state.inputValue;
                      return _.filter(options, o => o.title?.indexOf(q) > -1);
                    }}
                    onInputChange={(e, value) => {
                      // enter押下時(eventType === 'onKeyDown')にタイトルを空にしない。
                      const eventType = Utils.dig(e, 'dispatchConfig', 'phasedRegistrationNames', 'bubbled');
                      if (typeof value === 'string' && eventType !== null && eventType !== 'onKeyDown') {
                        adjustment.title = value;
                      }
                    }}
                    onChange={(_, selected) => {
                      if (selected && typeof selected !== 'string') {
                        adjustment.title = selected.title;
                      }
                    }}
                    openOnFocus
                    freeSolo
                    renderInput={params => (
                      <TheTextField
                        {...params}
                        label="タイトル"
                        placeholder="タイトルを入力"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    style={{ width: '100%' }}
                  />
                </AutocompleteBox>
              </Box>
              {isTitleEmpty() && <Error>タイトルを入力してください</Error>}
            </LeftSection>
            <LeftSection>
              <Box display="flex">
                <Box flexGrow={999}>
                  <FormControl style={{ width: '100%' }}>
                    <InputLabel id="duration">打ち合わせ時間</InputLabel>
                    <Select labelId="duration" value={adjustment.duration} onChange={e => handleChangeDuration(e)}>
                      {durations.map(d => (
                        <MenuItem value={d.value} key={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </LeftSection>
            <LeftSection style={{ marginBottom: '15px' }}>
              <ParticipantInput
                isLoading={isLoading}
                participants={adjustment.participants}
                calendars={calendars}
                onAdd={handleAddParticipant}
                onRemove={handleRemoveParticipant}
              />
              {isParticipantEmpty() && <Error>参加者を選択してください</Error>}
            </LeftSection>
            <LeftSection>
              <Box display="flex">
                <Box flexGrow={999}>
                  <PlaceInput
                    value={adjustment.place}
                    label="場所"
                    placeholder="場所を追加"
                    onInputChange={value => {
                      adjustment.place = value;
                      if (value.length === 0) {
                        adjustment.placeAddress = '';
                      }
                    }}
                    onChange={value => {
                      if (!value || typeof value === 'string') {
                        return;
                      }
                      adjustment.placeAddress = value.subText;
                    }}
                    helperText={adjustment.placeAddress}
                  />
                </Box>
              </Box>
            </LeftSection>
            <LeftSection>
              <Box flexGrow={999}>
                <TextField
                  label="メモ"
                  placeholder="メモを追加"
                  value={adjustment.description || ''}
                  multiline
                  onChange={e => {
                    adjustment.description = e.target.value;
                  }}
                  InputLabelProps={{ shrink: true }}
                  style={{ width: '100%' }}
                />
              </Box>
            </LeftSection>
            <LeftSection>
              <Box display="flex">
                <Box flexGrow={999}>
                  <FormControl style={{ width: '100%' }}>
                    <InputLabel shrink htmlFor="adjustment-meeting-type">
                      Web会議
                    </InputLabel>
                    <NativeSelect
                      value={adjustment.meetingType}
                      onChange={e => handleChangeMeetingType(e.target.value as AdjustmentMeetingType)}
                      inputProps={{
                        name: 'meeting_type',
                        id: 'adjustment-meeting-type'
                      }}
                    >
                      <option value="none">なし</option>
                      {calendars.some(c => c.provider === 'google') && <option value="google">Google Meet</option>}
                      <option value="zoom">Zoom</option>
                    </NativeSelect>
                  </FormControl>
                </Box>
              </Box>
              <Box>{renderZoomAuthDialog()}</Box>
            </LeftSection>
          </div>
          <LeftSection>
            <Box display="flex">
              <Box flexGrow={999}>
                <H2 style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', fontSize: '16px' }}>
                  候補日の選択
                  {isSuggestCandidatesLoading && <SuggestCandidatesLoading />}
                  {contextValue.isMobile && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<DateRangeIcon />}
                      onClick={() => setShowMobileCalendarDialog(true)}
                      style={{ marginLeft: 'auto', padding: '6px 12px' }}
                    >
                      カレンダーから選択
                    </Button>
                  )}
                </H2>
                <LeftSection>
                  <Box display="flex" alignItems="center">
                    <SuggestButton
                      size="small"
                      color="primary"
                      disabled={isSuggestLoading || isParticipantEmpty()}
                      variant="contained"
                      onClick={() => {
                        setIsSuggestCandidatesLoading(true);
                        handleSuggestCandidates(targetFromDate);
                      }}
                      ref={suggestButtonRef}
                    >
                      候補日を自動抽出
                    </SuggestButton>
                    <SuggestButton
                      size="small"
                      color="primary"
                      disabled={isSuggestLoading || isParticipantEmpty()}
                      variant="contained"
                      onClick={() => {
                        setShowSuggestSettingDialog(true);
                        window.clearTimeout(suggestCandidatesTimerID);
                        setSuggestCandidatesTimerID(
                          window.setTimeout(() => {
                            setIsSuggestCandidatesLoading(true);
                            handleSuggestCandidates(targetFromDate);
                          }, 1000)
                        );
                      }}
                      ref={suggestSettingButtonRef}
                    >
                      <SettingsIcon fontSize="small" style={{ color: '#fff' }} />
                    </SuggestButton>
                    {renderSuggestSettingDialog()}
                  </Box>
                  {isCandidatesEmpty() && <Error>候補日を選択してください</Error>}
                </LeftSection>
                <Typography style={{ fontSize: '10px', marginBottom: '10px' }}>選択中の候補日</Typography>
                {adjustment.candidates.length > 0 ? (
                  <>
                    {adjustment.candidates
                      .sort((a, b) => (a.start < b.start ? -1 : 1))
                      .map(c => (
                        <HoverActionBox
                          key={c.start.format()}
                          clearTitle="この候補を削除"
                          onClear={() => adjustment.removeCandidate(c)}
                        >
                          <Typography key={c.start.format()}>{c.timeText}</Typography>
                        </HoverActionBox>
                      ))}
                  </>
                ) : (
                  <NoCandidate>候補日なし</NoCandidate>
                )}
              </Box>
            </Box>
          </LeftSection>
          <LeftSection>
            <Box display="flex">
              <Box flexGrow={999}>
                {adjustment.key ? (
                  <Redirect to={`/?adjustment_key=${adjustment.key}#created`} />
                ) : (
                  <>
                    <LeftButtonWrap isMobile={contextValue.isMobile}>
                      {contextValue.isMobile && (
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          component={RouterLink}
                          to="/"
                          style={{ width: '100%', marginRight: 20, padding: 10, background: '#fff' }}
                        >
                          キャンセル
                        </Button>
                      )}
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={!isSubmitButtonAvailable()}
                        onClick={() => handleValidation()}
                        style={{ width: contextValue.isMobile ? '100%' : '100px', padding: 10 }}
                      >
                        作成
                      </Button>
                    </LeftButtonWrap>
                    {errorMessage && <Error color="error">{errorMessage}</Error>}
                    {isMultiParticipantPrevented() && (
                      <>
                        <ToDoMessage>複数名の参加者登録にはProプランが必要です</ToDoMessage>
                        {user?.aicalendarAdmin && (
                          <Link href="/organization/payments" target="_blank">
                            {trialUsed ? 'プランを見る' : '無料で試す'}
                          </Link>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </LeftSection>
          {!contextValue.isMobile && (
            <LeftSection>
              <HintBox>
                <HintTitle>
                  <EmojiObjectsIcon fontSize="small" />
                  <span>Hint</span>
                </HintTitle>
                <HintText>
                  <span>・カレンダーをクリックで候補日を入力</span>
                </HintText>
                <HintText>
                  <span>・</span>
                  <HintEventBox />
                  <span>をドラッグで日時を調整</span>
                </HintText>
                <HintText>
                  <span>・</span>
                  <HintEventBox />
                  <span>をダブルクリックで候補日を削除</span>
                </HintText>
              </HintBox>
            </LeftSection>
          )}
        </TheLeftBox>
      </TheLeft>
      {!contextValue.isMobile ? (
        <TheRight>
          <BigCalendar
            adjustment={adjustment}
            eventLists={eventLists}
            candidates={adjustment.candidates}
            travelTimes={travelTimes}
            calendars={calendars}
            date={date}
            isEventLoading={isEventLoading}
            onCandidatesChange={(candidates, travelTimes) => {
              adjustment.candidates = candidates;
              if (travelTimes) {
                setTravelTimes(travelTimes);
              }
            }}
            onDateChange={date => setDate(date)}
          />
        </TheRight>
      ) : (
        <>{renderMobileCalendarDialog()}</>
      )}
      {renderCandidateHelpDialog()}
      {needCoachMark && theLeftBoxRef && (
        <NewCoachMark
          theLeftBoxRef={theLeftBoxRef}
          inputFormRef={inputFormRef}
          suggestSettingButtonRef={suggestSettingButtonRef}
          suggestButtonRef={suggestButtonRef}
        />
      )}
    </Wrapper>
  );
});

export const AuthBackdrop = styled(Backdrop)`
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.8);
`;

export const TheReAuthButton = styled(Button)`
  span.MuiButton-label {
    display: block;
    position: relative;
    width: auto;
    padding-right: 25px;
    &:before {
      content: '';
      position: absolute;
      top: 8px;
      right: 2px;
      width: 6px;
      height: 6px;
      border-top: 2px solid #fff;
      border-right: 2px solid #fff;
      -webkit-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
`;

const TheLeft = styled(Left)<{ isMobile: boolean }>`
  width: 280px;
  margin: 0;
  padding-bottom: 24px;
  border-top: solid 10px ${muiTheme.palette.primary.main};

  ${props =>
    props.isMobile &&
    `
    width: 100vw;
  `}
`;

const TheLeftBox = styled.div<{ isMobile: boolean }>`
  margin-top: 24px;
  padding: 0 20px;
  max-height: calc(100vh - 64px - 24px - ${props => (props.isMobile ? '67px' : '24px')});
  overflow-y: scroll;
`;

const CloseButton = styled(CancelIcon)`
  color: #ddd;
`;

const TheTextField = styled(TextField)`
  font-size: 12px;
`;

const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-top: 6px;
  font-size: 10px;
`;

const ToDoMessage = styled(Typography).attrs({ color: 'textSecondary' })`
  font-size: 10px;
`;

const RuleHead = styled(Box)`
  margin-bottom: 25px;
  color: ${muiTheme.palette.primary.main};
  text-align: center;
`;

const RuleHeadIcon = styled.div`
  margin-bottom: 5px;
  transform: rotate(45deg);
`;

const RuleTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
`;

const RuleText = styled(Typography)`
  font-size: 11px;
  font-weight: 700;
`;

const RuleMain = styled(Box)<{ isMobile: boolean }>`
  margin: 0 70px;
  border-bottom: solid 1px ${props => props.theme.grayBorderColor};

  ${props =>
    props.isMobile &&
    `
    margin: 0 20px;
  `}
`;

const HourSelectSection = styled(Section)`
  .MuiSlider-rail,
  .MuiSlider-track,
  .MuiSlider-mark {
    height: 4px;
  }

  .MuiSlider-thumb {
    width: 15px;
    height: 15px;
    background-color: #fff;
    border: solid 4px ${muiTheme.palette.primary.main};
  }
`;

const RuleFormControl = styled(FormControl)`
  width: 100px;
  margin: 0 9px;
`;

const RuleInputLabel = styled(InputLabel)`
  font-size: 10px;
`;

const CandidateHelpBox = styled(Box)`
  padding: 20px 70px;
`;

const CandidateHelpTitle = styled(Typography)`
  font-weight: bold;
  margin-bottom: 20px;
`;

const CandidateHelpDescription = styled(Typography)`
  margin-top: 20px;
`;

const CandidateHelpImg = styled.img`
  border: 1px solid gray;
  width: 100%;
  min-height: 300px;
`;

export const AutocompleteBox = styled.div<{ withAlert?: string }>`
  width: 100%;
  .MuiAutocomplete-inputRoot.MuiFilledInput-root {
    padding-top: 6px;
  }

  ${props =>
    props.withAlert === 'true' &&
    `
    input::placeholder {
      color: ${muiTheme.palette.error.main};
      opacity: 1;
    }
    .MuiInput-underline:before {
      border-bottom-color: ${muiTheme.palette.error.main};
    }
  `}
`;

const LeftSection = styled(Box)`
  margin-bottom: 20px;
`;

const SuggestButton = styled(FlatButton)`
  min-width: auto;
  padding: 12px 15px;
  line-height: 20px;

  &:first-child {
    margin-right: 1px;
    flex-grow: 1;
  }
`;

const NoCandidate = styled.div`
  padding: 10px 12px;
  color: #909090;
  border: solid 1px;
  border-radius: 3px;
  font-size: 12px;
`;

const ToggleButton = styled.input<{ isMobile: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  -ms-appearance: none;
  -moz-appearance: none;
  display: inline-block;
  vertical-align: top;
  width: 40px;
  height: 40px;
  margin: 0 5px;
  border-radius: 3px;
  border: 1px solid #aaa;
  background-color: #ffffff;
  text-align: center;
  line-height: 38px;
  font-size: 13px;
  cursor: pointer;
  transition: 200ms;

  &:focus:hover {
    outline: none;
  }

  &:active {
    height: 35px;
    margin-top: 2px;
    transition: none;
  }

  &:not(:checked)::after {
    display: inline;
    content: attr(data-off-label);
    color: #888;
    transition: none;
  }

  &:checked::after {
    display: block;
    border-radius: 2px;
    content: attr(data-on-label);
    color: #ffffff;
    background-color: ${muiTheme.palette.primary.main};
    transition: 400ms;
  }

  &:active::after {
    transition: none;
  }

  ${props =>
    props.isMobile &&
    `
    width: 32px;
    height: 34px;
    line-height: 32px;
  `}
`;

const LabelSuggest = styled(Typography)`
  display: flex;
  margin-block-start: 0.5em;
  color: ${muiTheme.palette.primary.main};
  font-size: 12px;
  font-weight: 700;
`;

const LabelSuggestDescription = styled(Typography)<{ rotate: string }>`
  margin: -10px 0 20px;
  color: ${muiTheme.palette.primary.main};
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  text-align: center;

  .text {
    display: inline-block;
    position: relative;
    padding: 4px 20px 4px 8px;
    background: #fff;
    cursor: pointer;
    transition: ease 0.3s;

    &:hover {
      opacity: 0.6;
    }

    .icon {
      position: absolute;
      top: -3px;
      right: -3px;
      transform: rotate(${props => props.rotate}deg);
    }
  }
`;

const RuleDetail = styled(Box)<{ opened: string }>`
  overflow: hidden;
  height: 0;
  transition: ease-in-out 0.4s;

  ${props =>
    props.opened === 'true' &&
    `
    height: 96px;
  `}
`;

const LeftButtonWrap = styled.div<{ isMobile: boolean }>`
  text-align: right;

  ${props =>
    props.isMobile &&
    `
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    width: 100%;
    padding: 10px 16px;
    background: #f9f9f9;
  `}
`;

const MobileCalendarButtonWrap = styled(LeftButtonWrap)`
  position: absolute;
`;

const HintBox = styled(Box)`
  padding: 4px;
  background: #fffdf0;
  border: solid 1px #eee7c0;
  border-radius: 3px;
`;

const HintTitle = styled(Typography)`
  display: flex;
  align-items: center;
  color: #d4c56e;
  font-size: 10px;
  font-weight: bold;
`;

const HintText = styled(Typography)`
  display: flex;
  align-items: center;
  padding-left: 5px;
  color: #606060;
  font-size: 10px;
`;

const HintEventBox = styled.div`
  width: 16px;
  height: 8px;
  margin-right: 3px;
  background: rgba(36, 53, 101, 0.2);
  border: 1px dashed #243565;
  border-radius: 3px;
`;

const TheRight = styled(Right)`
  padding: 12px 20px 0 24px;
  border-left: solid 1px ${props => props.theme.grayBorderColor};

  .rbc-toolbar button {
    padding: 0.275rem 1rem;
  }
`;

const SuggestCandidatesLoading = styled(OriginalCircularProgress).attrs({ size: 16 })`
  color: ${muiTheme.palette.primary.main};
  margin-left: 10px;
`;

const ZoomAuthDialog = styled(Dialog)``;

const ZoomAuthContent = styled(Box)`
  padding: 64px 56px 14px;
`;

const ZoomAuthButtonWrap = styled(Box)`
  display: flex;
  justify-content: center;
  background-color: #f9f9f9;
  padding: 14px 0;

  & button:first-child {
    margin-right: 10px;
  }

  & button {
    width: 105px;
  }
`;

export default AdjustmentsNew;
